import {KpiCalculationError, ExpertSystemHierarchyNodeType} from '@hconnect/common/types'
import {completionScoreBandColor} from '@hconnect/common/utils'
import ModeCommentOutlinedIcon from '@mui/icons-material/ModeCommentOutlined'
import {Typography, CircularProgress, Box, IconButton} from '@mui/material'
import {FC} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, VALIDATION_TRANSLATIONS_KEY} from '../consts'
import {MonthData} from '../types'
import {isKilnOrMillLevel} from '../utils'

import {EditableMonthCell} from './EditableMonthCell'

type MonthCellProps = {
  hierarchyId: string
  monthData: MonthData
  month: string
  isEditMode: boolean
  onMonthValueChange?: (
    hierarchyId: string,
    month: string,
    value?: number | KpiCalculationError
  ) => void
  canEdit?: boolean
  setNodeMonth: (id: string) => void
  itemType: ExpertSystemHierarchyNodeType
  onCellClick?: (value: number) => void
  onCommentClick?: () => void
  year: number
}

export const MonthCell: FC<MonthCellProps> = ({
  hierarchyId,
  monthData,
  month,
  onMonthValueChange,
  isEditMode,
  canEdit,
  setNodeMonth,
  itemType,
  onCellClick,
  year,
  onCommentClick
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {value, kpiCalculationError, isEditedManually, isDirty, hasComments} = monthData || {}

  if (isEditMode && onMonthValueChange && canEdit) {
    return (
      <EditableMonthCell
        itemId={hierarchyId}
        year={year}
        month={month}
        value={value}
        kpiCalculationError={kpiCalculationError}
        onValueChange={onMonthValueChange}
        setNodeMonth={setNodeMonth}
        isEditedManually={isEditedManually}
      />
    )
  }

  const getDisplayValue = () => {
    if (value !== undefined) {
      return `${value}%`
    }
    if (kpiCalculationError === KpiCalculationError.NoKpiValue) {
      return isKilnOrMillLevel(itemType)
        ? t(VALIDATION_TRANSLATIONS_KEY[KpiCalculationError.NoKpiValue])
        : '-'
    }
    if (kpiCalculationError) {
      return t(VALIDATION_TRANSLATIONS_KEY[kpiCalculationError])
    }
    return kpiCalculationError
  }

  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onCommentClick?.()
  }

  return (
    <Box
      display="inline-flex"
      alignItems="center"
      gap={0.5}
      sx={{cursor: onCellClick ? 'pointer' : 'default'}}
      onClick={() => value !== undefined && onCellClick?.(value)}
    >
      <Typography
        component="span"
        display="inline-flex"
        alignItems="center"
        sx={{
          color:
            isDirty || value === null || value === undefined
              ? 'text.disabled'
              : completionScoreBandColor(value)
        }}
      >
        {getDisplayValue()}
      </Typography>
      {onCommentClick && (
        <IconButton
          data-test-id="expert-system-usage-comment-kpi"
          color={hasComments ? 'primary' : 'default'}
          onClick={handleCommentClick}
        >
          <ModeCommentOutlinedIcon sx={{fontSize: 14}} />
        </IconButton>
      )}
      {isDirty && <CircularProgress size={16} />}
    </Box>
  )
}
